// Comms: messages the office sends to engineers and staff, with read and
// acknowledged tracking (like NFUK's Comms Dispatch). The body is a list of
// blocks; the same rules check it in the API and the builder.

// Who a message is from (the header on the inbox).
export const COMM_DEPARTMENTS = Object.freeze([
  { value: 'operations', label: 'Operations' },
  { value: 'health_safety', label: 'Health & Safety' },
  { value: 'fleet', label: 'Fleet' },
  { value: 'hr', label: 'HR' },
  { value: 'it', label: 'IT' },
  { value: 'management', label: 'Management' },
]);
// Who the platform's own messages are from (never offered to the office).
export const COMM_SYSTEM_DEPARTMENTS = Object.freeze([{ value: 'helpdesk', label: 'Helpdesk' }, { value: 'stock', label: 'Stock' }, { value: 'surveys', label: 'Surveys' }]);
export const COMM_DEPARTMENT_LABEL = Object.freeze(Object.fromEntries([...COMM_DEPARTMENTS, ...COMM_SYSTEM_DEPARTMENTS].map((d) => [d.value, d.label])));

export const COMM_PRIORITIES = Object.freeze(['normal', 'urgent']);

// A message is text (one editor per stretch, with headings, bold, lists,
// links and tables) with attachments between: pictures, video links, PDFs.
// heading / text / bullets / table are the older separate blocks: messages
// sent with them still show, and the editor turns them into text.
export const COMM_BLOCK_TYPES = Object.freeze([
  { value: 'rich', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video link' },
  { value: 'pdf', label: 'PDF' },
]);

// --- Rich text: the editor's document (ProseMirror JSON), kept to what the
// toolbar can make. Anything else is dropped, so a message can only ever
// hold text, never markup or scripts.
const RICH_NODES = {
  doc: { content: true },
  paragraph: { content: true },
  heading: { content: true, attrs: (a) => ({ level: [2, 3].includes(a?.level) ? a.level : 2 }) },
  bulletList: { content: true },
  orderedList: { content: true, attrs: (a) => ({ start: Number.isInteger(a?.start) && a.start > 0 && a.start < 10000 ? a.start : 1 }) },
  listItem: { content: true },
  blockquote: { content: true },
  table: { content: true },
  tableRow: { content: true },
  tableHeader: { content: true },
  tableCell: { content: true },
  hardBreak: {},
  horizontalRule: {},
  text: {},
};
export const safeHref = (href) => {
  const h = String(href || '').trim();
  return /^(https?:\/\/|mailto:|tel:)/i.test(h) && h.length <= 2000 ? h : null;
};
function cleanMarks(marks) {
  const out = [];
  for (const m of Array.isArray(marks) ? marks : []) {
    if (['bold', 'italic', 'underline', 'strike'].includes(m?.type)) out.push({ type: m.type });
    else if (m?.type === 'link') {
      const href = safeHref(m.attrs?.href);
      if (href) out.push({ type: 'link', attrs: { href } });
    }
  }
  return out;
}
function cleanNode(n, depth, budget) {
  if (!n || typeof n !== 'object' || depth > 12 || budget.left <= 0) return null;
  const rule = RICH_NODES[n.type];
  if (!rule) return null;
  budget.left -= 1;
  const out = { type: n.type };
  if (n.type === 'text') {
    const text = String(n.text ?? '').slice(0, 10000);
    if (!text) return null;
    out.text = text;
    const marks = cleanMarks(n.marks);
    if (marks.length) out.marks = marks;
    return out;
  }
  if (rule.attrs) out.attrs = rule.attrs(n.attrs);
  if (rule.content) out.content = (Array.isArray(n.content) ? n.content : []).map((c) => cleanNode(c, depth + 1, budget)).filter(Boolean);
  return out;
}
export function cleanRich(doc) {
  const out = cleanNode(doc, 0, { left: 5000 });
  return out?.type === 'doc' ? out : null;
}
// The words in a rich-text document (previews, notifications, "is it empty?").
const SPACED = ['doc', 'bulletList', 'orderedList', 'table', 'tableRow', 'blockquote', 'listItem'];
export function richText(node) {
  if (!node) return '';
  if (node.type === 'text') return node.text;
  if (node.type === 'hardBreak') return '\n';
  return (node.content || []).map(richText).join(SPACED.includes(node.type) ? ' ' : '');
}

// The older separate blocks as one rich-text document (for the editor).
const para = (t) => ({ type: 'paragraph', content: t ? [{ type: 'text', text: t }] : [] });
export function blocksToRich(blocks) {
  const content = [];
  for (const b of blocks || []) {
    if (b.type === 'heading') content.push({ type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: b.text }] });
    else if (b.type === 'text') {
      for (const p of String(b.text).split(/\n{2,}/)) {
        content.push({ type: 'paragraph', content: p.split('\n').flatMap((l, i) => [...(i ? [{ type: 'hardBreak' }] : []), ...(l ? [{ type: 'text', text: l }] : [])]) });
      }
    } else if (b.type === 'bullets') content.push({ type: 'bulletList', content: b.items.map((i) => ({ type: 'listItem', content: [para(i)] })) });
    else if (b.type === 'table') {
      content.push({ type: 'table', content: b.rows.map((r, ri) => ({ type: 'tableRow', content: r.map((c) => ({ type: b.header && ri === 0 ? 'tableHeader' : 'tableCell', content: [para(c)] })) })) });
    }
  }
  return { type: 'doc', content: content.length ? content : [para('')] };
}
// A body for the editor: neighbouring old text blocks become one text block.
export function toEditorBlocks(blocks) {
  const out = [];
  let run = [];
  const flush = () => { if (run.length) out.push({ type: 'rich', doc: blocksToRich(run) }); run = []; };
  for (const b of blocks || []) {
    if (['heading', 'text', 'bullets', 'table'].includes(b.type)) run.push(b);
    else { flush(); out.push(b); }
  }
  flush();
  return out.length ? out : [{ type: 'rich', doc: blocksToRich([]) }];
}

// Videos play inside the message only from these sites; any other link opens
// in a new tab.
const EMBED_HOSTS = [/(^|\.)youtube\.com$/, /^youtu\.be$/, /(^|\.)vimeo\.com$/, /\.sharepoint\.com$/, /(^|\.)microsoftstream\.com$/];

export function videoEmbed(url) {
  let u;
  try { u = new URL(url); } catch { return null; }
  if (u.protocol !== 'https:') return null;
  const host = u.hostname.toLowerCase();
  if (!EMBED_HOSTS.some((h) => h.test(host))) return null;
  if (/youtube\.com$/.test(host)) {
    const id = u.searchParams.get('v') || (u.pathname.match(/^\/(?:embed|shorts|live)\/([\w-]{6,})/) || [])[1];
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (host === 'youtu.be') {
    const id = u.pathname.slice(1).split('/')[0];
    return /^[\w-]{6,}$/.test(id) ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (/vimeo\.com$/.test(host)) {
    const id = (u.pathname.match(/(\d{5,})/) || [])[1];
    return id ? `https://player.vimeo.com/video/${id}` : null;
  }
  // SharePoint / Stream: their own embed links.
  return u.href;
}

const text = (v, max) => String(v ?? '').replace(/\r\n/g, '\n').slice(0, max);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// Tidies a body from the builder; returns { blocks, error }. Empty blocks are
// dropped. Images are uploaded first and referenced by id.
export function cleanBlocks(input) {
  if (!Array.isArray(input)) return { blocks: [], error: 'The message is empty' };
  const blocks = [];
  for (const b of input.slice(0, 100)) {
    const type = b?.type;
    if (type === 'heading' || type === 'text') {
      const t = text(b.text, type === 'heading' ? 200 : 10000).trim();
      if (t) blocks.push({ type, text: t });
    } else if (type === 'bullets') {
      const items = (Array.isArray(b.items) ? b.items : []).map((i) => text(i, 500).trim()).filter(Boolean).slice(0, 50);
      if (items.length) blocks.push({ type, items });
    } else if (type === 'table') {
      const rows = (Array.isArray(b.rows) ? b.rows : []).slice(0, 50)
        .map((r) => (Array.isArray(r) ? r : []).slice(0, 8).map((c) => text(c, 300).trim()));
      const kept = rows.filter((r) => r.some(Boolean));
      if (kept.length) {
        const width = Math.max(...kept.map((r) => r.length));
        blocks.push({ type, header: b.header !== false, rows: kept.map((r) => [...r, ...Array(width - r.length).fill('')]) });
      }
    } else if (type === 'rich') {
      const doc = cleanRich(b.doc);
      if (doc && richText(doc).trim()) blocks.push({ type, doc });
    } else if (type === 'image') {
      const imageId = String(b.imageId || '').toLowerCase();
      if (UUID.test(imageId)) blocks.push({ type, imageId, caption: text(b.caption, 300).trim() });
    } else if (type === 'pdf') {
      const fileId = String(b.fileId || '').toLowerCase();
      if (UUID.test(fileId)) blocks.push({ type, fileId, name: text(b.name, 200).trim() || 'Document.pdf', caption: text(b.caption, 300).trim() });
    } else if (type === 'video') {
      const url = text(b.url, 1000).trim();
      if (!url) continue;
      let ok = false;
      try { ok = new URL(url).protocol === 'https:'; } catch { ok = false; }
      if (!ok) return { blocks: [], error: 'A video link must start with https://' };
      blocks.push({ type, url, caption: text(b.caption, 300).trim() });
    }
  }
  if (!blocks.length) return { blocks, error: 'The message is empty' };
  return { blocks, error: null };
}

// A line of the message for previews and notifications.
export function blocksPreview(blocks, max = 140) {
  const parts = [];
  for (const b of blocks || []) {
    if (b.type === 'text' || b.type === 'heading') parts.push(b.text);
    else if (b.type === 'rich') parts.push(richText(b.doc));
    else if (b.type === 'bullets') parts.push(b.items.join(', '));
    if (parts.join(' ').length > max) break;
  }
  const s = parts.join(' ').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// "Installers", "Job Controllers", but "Operations" (already plural).
export const rolePlural = (label) => (/s$/i.test(label) ? label : `${label}s`);
